import { RuleItem } from "../interfaces/RuleItem";
import { RuleItemSelectOptions } from "../interfaces/RuleItemSelectOptions";
import { IRulesDefinition } from "../interfaces/IRulesDefinition";
import { RuleItemConfig } from "../interfaces/RuleItemConfig";

export const CURRENT_GAME_KEY = "currentGame";
export const NO_CLASS_CHOSEN = "No Class Chosen";
export type RankAction = "inc" | "dec" | "reset";
export enum RuleModuleType {
  ItemOnly = "item-only",
  ItemAndSource = "item-and-source",
  RankOnly = "rank-only",
  RankAndSource = "rank-and-source",
  SelectAndRank = "select-and-rank",
  SelectOnly = "select-only",
}
export type BonusDetailType =
  | "bonus-from-source"
  | "bonus-targeted"
  | "bonus-progression"
  | "bonus-unknown"
  | "rank-improvement";
export type BonusCalculationStatus = "started" | "done";
export type DiceType =
  | "d2"
  | "d3"
  | "d4"
  | "d6"
  | "d8"
  | "d10"
  | "d12"
  | "d20"
  | "d30"
  | "d100";

export enum ProgressionType {
  Basic = "basic",
  Dice = "dice",
  Rank = "rank",
  Allocate = "allocate",
}

export const examples: string[] = [
  "addProficiency Charm chm cha",
  "addProficiency Appraise app int wis",
  "addProficiency 'First Aid' fad dex wis",
  "addAttribute Scrap scr",
  "updateAttribute cha inc",
  "updateAttribute con dec",
  "updateProficiency anh inc",
  "updateProficiency arc inc",
  "deleteAttribute str",
  "deleteProficiency ath",
];

const defaultRuleItemConfig: RuleItemConfig = { min: 0, max: 1000, default: 0 };

const attributes: RuleItem[] = [
  {
    name: "Strength",
    key: "str",
    details: {},
  },
  {
    name: "Dexterity",
    key: "dex",
    details: {},
  },
  {
    name: "Constitution",
    key: "con",
    details: {},
  },
  {
    name: "Intelligence",
    key: "int",
    details: {},
  },
  {
    name: "Wisdom",
    key: "wis",
    details: {},
  },
  {
    name: "Charisma",
    key: "cha",
    details: {},
  },
];

const proficiencies: RuleItem[] = [
  {
    name: "Acrobatics",
    key: "acr",
    details: {
      bonusCalculator: {
        sources: [{ source: "dex" }],
      },
    },
  },
  {
    name: "Animal Handling",
    key: "anh",
    details: {
      bonusCalculator: {
        sources: [{ source: "wis" }],
      },
    },
  },
  {
    name: "Arcana",
    key: "arc",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Athletics",
    key: "ath",
    details: {
      bonusCalculator: {
        sources: [{ source: "str" }],
      },
    },
  },
  {
    name: "Deception",
    key: "dec",
    details: {
      bonusCalculator: {
        sources: [{ source: "cha" }],
      },
    },
  },
  {
    name: "History",
    key: "his",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Insight",
    key: "ins",
    details: {
      bonusCalculator: {
        sources: [{ source: "wis" }],
      },
    },
  },
  {
    name: "Intimidation",
    key: "itd",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Investigation",
    key: "inv",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Medicine",
    key: "med",
    details: {
      bonusCalculator: {
        sources: [{ source: "wis" }],
      },
    },
  },
  {
    name: "Nature",
    key: "nat",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Perception",
    key: "per",
    details: {
      bonusCalculator: {
        sources: [{ source: "wis" }],
      },
    },
  },
  {
    name: "Performance",
    key: "prf",
    details: {
      bonusCalculator: {
        sources: [{ source: "cha" }],
      },
    },
  },
  {
    name: "Persuasion",
    key: "psn",
    details: {
      bonusCalculator: {
        sources: [{ source: "cha" }],
      },
    },
  },
  {
    name: "Religion",
    key: "rel",
    details: {
      bonusCalculator: {
        sources: [{ source: "int" }],
      },
    },
  },
  {
    name: "Sleight of Hand",
    key: "soh",
    details: {
      bonusCalculator: {
        sources: [{ source: "dex" }],
      },
    },
  },
  {
    name: "Stealth",
    key: "ste",
    details: {
      bonusCalculator: {
        sources: [{ source: "dex" }],
      },
    },
  },
  {
    name: "Survival",
    key: "sur",
    details: {
      bonusCalculator: {
        sources: [{ source: "wis" }],
      },
    },
  },
];

const classes: RuleItem[] = [
  {
    name: "Rogue",
    key: "rog",
    details: {
      bonusForTarget: [
        {
          target: "ste",
          value: 1,
        },
        {
          name: "Ninja",
          target: "acr",
          value: 1,
          perceived: 2,
        },
        { target: "arm", value: 1 },
        { target: "fort", value: 1 },
      ],
    },
  },
  {
    name: "Paladin",
    key: "pal",
    details: {
      bonusForTarget: [
        {
          target: "rel",
          value: 1,
        },
        {
          target: "med",
          value: 1,
          perceived: 2,
        },
        { target: "bab", value: 2 },
        { target: "will", value: 1 },
      ],
    },
  },
  {
    name: "Bard",
    key: "brd",
    details: {
      bonusForTarget: [
        {
          target: "prf",
          value: 1,
        },
        {
          target: "anh",
          value: 1,
          perceived: 2,
        },
        { target: "fort", value: 1 },
      ],
    },
  },
  {
    name: "Mage",
    key: "mag",
    details: {
      bonusForTarget: [
        {
          target: "arc",
          value: 1,
        },
        {
          target: "ins",
          value: 1,
          perceived: 2,
        },
        { target: "ran", value: 1 },
        { target: "will", value: 1 },
      ],
    },
  },
];

const traits: RuleItem[] = [
  {
    name: "Melee Attack",
    key: "mel",
    details: {
      bonusCalculator: { sources: [{ source: "bab" }, { source: "str" }] },
    },
  },
  {
    name: "Ranged Attack",
    key: "ran",
    details: {
      bonusCalculator: { sources: [{ source: "bab" }, { source: "dex" }] },
    },
  },
  {
    name: "Armor Class",
    key: "arm",
    details: {
      bonusCalculator: { sources: [{ source: "fort" }, { source: "str" }] },
    },
  },
  {
    name: "Will Save",
    key: "wil",
    details: { bonusCalculator: { sources: [{ source: "will" }] } },
  },
  {
    name: "Fortitude Save",
    key: "frt",
    details: { bonusCalculator: { sources: [{ source: "fort" }] } },
  },
];

const base_stats: RuleItem[] = [
  { name: "BAB", key: "bab", details: {} },
  { name: "Will", key: "will", details: {} },
  { name: "Fortitude", key: "fort", details: {} },
];

const space_stats: RuleItem[] = [
  { name: "Strength", key: "sgt", details: {} },
  { name: "Speed", key: "spe", details: {} },
  { name: "Intellect", key: "itl", details: {} },
  { name: "Combat", key: "cmt", details: {} },
];

const backgrounds: RuleItem[] = [
  // TODO: apply bonuses at level 0 and only at zero
  {
    name: "Teamster",
    key: "tea",
    details: {
      statProgression: {
        fea: { levels: [], startingBonus: 35 },
        amr: { levels: [], startingBonus: 35 },
      },
      bonusForTarget: [
        {
          target: "sgt",
          value: 5,
          applyOnce: true,
        },
        {
          target: "spe",
          value: 5,
          applyOnce: true,
        },
      ],
    },
  },
  {
    name: "Android",
    key: "and",
    details: {
      statProgression: {
        san: { levels: [], startingBonus: 20 },
        fea: { levels: [], startingBonus: 85 },
        bod: { levels: [], startingBonus: 40 },
        amr: { levels: [], startingBonus: 25 },
      },
      bonusForTarget: [
        {
          target: "spe",
          value: 5,
          applyOnce: true,
        },
        {
          target: "itl",
          value: 5,
          applyOnce: true,
        },
      ],
    },
  },
  {
    name: "Scientist",
    key: "sci",
    details: {
      statProgression: {
        san: { levels: [], startingBonus: 40 },
        fea: { levels: [], startingBonus: 25 },
        bod: { levels: [], startingBonus: 25 },
      },
      bonusForTarget: [
        {
          target: "itl",
          value: 10,
          applyOnce: true,
        },
      ],
    },
  },
  {
    name: "Marine",
    key: "mar",
    details: {
      statProgression: {
        san: { levels: [], startingBonus: 25 },
        bod: { levels: [], startingBonus: 35 },
        amr: { levels: [], startingBonus: 40 },
      },
      bonusForTarget: [
        {
          target: "cmt",
          value: 5,
          applyOnce: true,
        },
        {
          // TODO: make conditional toggle
          target: "cmt",
          value: 5,
          applyOnce: true,
        },
      ],
    },
  },
];

const backgroundSelectOptions: RuleItemSelectOptions = {
  name: "Saves",
  key: "saves",
  maxItemCount: 1,
  defaultMaxStat: 85,
  statDefinitions: {
    san: {
      name: "Sanity",
      default: 30,
      progression: {
        defaultMultiplier: 6,
        progressionPerLevel: ProgressionType.Allocate,
        skipFirstLevel: false,
      },
    },
    fea: {
      name: "Fear",
      default: 30,
      progression: {
        defaultMultiplier: 6,
        progressionPerLevel: ProgressionType.Allocate,
        skipFirstLevel: false,
      },
    },
    bod: {
      name: "Body",
      default: 30,
      progression: {
        defaultMultiplier: 6,
        progressionPerLevel: ProgressionType.Allocate,
        skipFirstLevel: false,
      },
    },
    amr: {
      name: "Armor",
      default: 30,
      progression: {
        defaultMultiplier: 6,
        progressionPerLevel: ProgressionType.Allocate,
        skipFirstLevel: false,
      },
    },
  },
};

const spaceSelectOptions: RuleItemSelectOptions = {
  name: "Saves",
  key: "saves",
  maxItemCount: 1,
  defaultMaxStat: 85,
  statDefinitions: {
    san: {
      name: "Sanity",
      default: 30,
    },
    fea: {
      name: "Fear",
      default: 30,
    },
    bod: {
      name: "Body",
      default: 30,
    },
    amr: {
      name: "Armor",
      default: 30,
    },
  },
};

const classSelectOptions: RuleItemSelectOptions = {
  name: "Class",
  key: "classes",
};

export const DEFAULT_STATE: IRulesDefinition = {
  ruleModules: [
    [
      {
        moduleName: "Attributes",
        ruleItemName: "Attribute",
        key: "att",
        moduleType: RuleModuleType.RankOnly,
        list: attributes,
        config: {
          min: 0,
          max: 20,
          default: 10,
          baseRank: 10,
        },
      },
      {
        moduleName: "Traits",
        ruleItemName: "Trait",
        key: "tra",
        moduleType: RuleModuleType.ItemAndSource,
        list: traits,
        config: defaultRuleItemConfig,
      },
    ],
    [
      {
        moduleName: "Classes",
        ruleItemName: "Class",
        key: "cla",
        moduleType: RuleModuleType.SelectAndRank,
        list: classes,
        config: {
          min: 0,
          max: 10,
          default: 0,
          selectOptions: classSelectOptions,
          default_class: "noc",
        },
      },
      {
        moduleName: "Background",
        ruleItemName: "Background",
        key: "bck",
        moduleType: RuleModuleType.SelectAndRank,
        list: backgrounds,
        config: {
          min: -1,
          max: 100,
          default: -1,
          selectOptions: backgroundSelectOptions,
        },
      },
      {
        moduleName: "Proficiencies",
        ruleItemName: "Proficiency",
        key: "pro",
        moduleType: RuleModuleType.RankAndSource,
        list: proficiencies,
        config: {
          min: 0,
          max: 10,
          default: 0,
        },
      },
    ],
    [
      {
        moduleName: "Base Stats",
        ruleItemName: "Base Stat",
        key: "bss",
        moduleType: RuleModuleType.ItemOnly,
        list: base_stats,
        config: defaultRuleItemConfig,
      },
    ],
  ],
};

const GAME_TWO: IRulesDefinition = {
  ruleModules: [
    [
      {
        moduleName: "Abilities",
        ruleItemName: "Ability",
        key: "att",
        moduleType: RuleModuleType.RankOnly,
        list: attributes,
        config: {
          min: 0,
          max: 100,
          default: 50,
          baseRank: 50,
        },
      },
      {
        moduleName: "Derived Stats",
        ruleItemName: "Derived Stat",
        key: "tra",
        moduleType: RuleModuleType.ItemAndSource,
        list: traits,
        config: defaultRuleItemConfig,
      },
    ],
    [
      {
        moduleName: "Jobs",
        ruleItemName: "Job",
        key: "cla",
        moduleType: RuleModuleType.SelectAndRank,
        list: classes,
        config: {
          min: 0,
          max: 10,
          default: 0,
          selectOptions: classSelectOptions,
          default_class: "noc",
        },
      },
      {
        moduleName: "Careers",
        ruleItemName: "Career",
        key: "bck",
        moduleType: RuleModuleType.SelectAndRank,
        list: backgrounds,
        config: {
          min: 0,
          max: 10,
          default: 0,
          selectOptions: backgroundSelectOptions,
        },
      },
      {
        moduleName: "Skills",
        ruleItemName: "Skill",
        key: "pro",
        moduleType: RuleModuleType.RankAndSource,
        list: proficiencies,
        config: {
          min: 0,
          max: 100,
          default: 20,
        },
      },
    ],
    [
      {
        moduleName: "Base Levels",
        ruleItemName: "Base Level",
        key: "bss",
        moduleType: RuleModuleType.ItemOnly,
        list: base_stats,
        config: defaultRuleItemConfig,
      },
    ],
  ],
};

const GAME_THREE: IRulesDefinition = {
  // TODO: xpModule keys are not checked for uniqueness against ruleModule keys yet
  xpModules: [
    {
      name: "Health",
      xpName: "HP",
      hideIfSpent: false,
      key: "health",
      details: {
        bonusCalculator: { sources: [{ source: "sgt", multiplier: 2 }] },
      },
      config: { min: 0, max: 100, default: 0 },
    },
    {
      name: "Current Health",
      xpName: "HP",
      hideIfSpent: false,
      key: "currenthealth",
      details: {},
      config: { min: 0, max: 100, default: 0 },
    },
    {
      name: "Major Improvements",
      xpName: "Major Improvement",
      hideIfSpent: true,
      key: "mji",
      details: {},
      config: { min: 0, max: 100, default: 0 },
      usageRules: {
        improvementsForModules: {
          stats: {
            upgrades: [
              { upgradeType: "value", value: 5 },
              { upgradeType: "value", value: 3 },
            ],
            type: "no-stacking",
          },
          saves: {
            upgrades: [
              { upgradeType: "value", value: 4 },
              { upgradeType: "value", value: 4 },
            ],
            type: "no-stacking",
          },
        },
        improvementsLimit: 1,
      },
    },
    {
      name: "Minor Improvements",
      xpName: "Minor Improvement",
      hideIfSpent: true,
      key: "mni",
      details: {},
      config: { min: 0, max: 100, default: 0 },
      usageRules: {
        improvementsForModules: {
          reso: {
            upgrades: [{ upgradeType: "value", value: 1 }],
            type: "no-stacking",
          },
          // TODO: remove a phobia or addiction
          stress: {
            upgrades: [{ upgradeType: "value", value: 0 }],
            type: "no-stacking",
          },
        },
        improvementsLimit: 1,
      },
    },
    {
      name: "Skill Points",
      xpName: "Skill Point",
      hideIfSpent: false,
      key: "skp",
      details: {},
      config: { min: 0, max: 100, default: 0 },
    },
    {
      name: "Stress",
      xpName: "Stress",
      hideIfSpent: false,
      key: "stress",
      details: {},
      config: { min: 0, max: 100, default: 2 },
    },
    {
      name: "Resolve",
      xpName: "Resolve",
      hideIfSpent: false,
      key: "reso",
      details: {},
      config: { min: 0, max: 100, default: 0 },
    },
    {
      name: "Initial Stats Allocation",
      xpName: "Initial Stat Roll",
      hideIfSpent: true,
      key: "isa",
      details: {},
      config: { min: 1, max: 1, default: 1 },
      usageRules: {
        improvementsForModules: {
          stats: {
            upgrades: [{ upgradeType: "add-dice", diceType: "d10", value: 6 }],
            type: "apply-to-each-and-choose",
          },
        },
        improvementsLimit: 0,
      },
    },
    {
      name: "Initial Credits",
      xpName: "Initial Credits",
      hideIfSpent: true,
      key: "inicr",
      details: {},
      config: { min: 1, max: 1, default: 1 },
      usageRules: {
        improvementsForModules: {
          cr: {
            upgrades: [
              {
                upgradeType: "add-dice",
                diceType: "d10",
                value: 5,
                multiplier: 10,
              },
            ],
            type: "no-stacking",
          },
        },
        improvementsLimit: 0,
      },
    },
    {
      name: "Credits",
      xpName: "Credit",
      hideIfSpent: false,
      key: "cr",
      details: {},
      config: { min: 0, max: 100, default: 0, rankMultiplier: 5 },
    },
  ],
  skillTreeModules: {
    // TODO: key "skills"
  },
  effectModules: {
    // TODO: phobias & addictions, equipment, other effects
  },
  ruleModules: [
    [
      {
        moduleName: "Abilities",
        ruleItemName: "Ability",
        key: "att",
        moduleType: RuleModuleType.RankOnly,
        list: attributes,
        config: {
          min: 0,
          max: 20,
          default: 10,
          baseRank: 10,
        },
      },
    ],
    [
      {
        moduleName: "Class",
        ruleItemName: "Class",
        key: "cls",
        moduleType: RuleModuleType.SelectAndRank,
        list: backgrounds,
        config: {
          min: -1,
          max: 100,
          default: -1,
          selectOptions: spaceSelectOptions,
        },
        levelUpOptions: [
          /**
           * TODO: instead of adding a rank to each of these for each level up,
           * multiply the value by the sum of the ranks in the module,
           * then apply it to the current XP modules for the character
           */
          {
            key: "mji",
            value: 1,
          },
          {
            key: "mni",
            value: 1,
          },
          {
            key: "skp",
            value: 2,
          },
        ],
      },
    ],
    [
      {
        moduleName: "Stats",
        ruleItemName: "Stat",
        key: "stats",
        // moduleType: RuleModuleType.RankOnly,
        moduleType: RuleModuleType.ItemOnly,
        list: space_stats,
        config: defaultRuleItemConfig,
      },
    ],
  ],
};

export const DEFAULT_GAMES: { [game: string]: IRulesDefinition } = {
  default: DEFAULT_STATE,
  two: GAME_TWO,
  three: GAME_THREE,
};
